import React from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { 
  Heart, Users, Eye, Target, 
  ArrowRight, Phone, Search, MessageCircle, AlertTriangle,
  CheckCircle, Building2, Brain, Sparkles, Quote
} from 'lucide-react';

const teamMembers = [
  {
    name: "Nelson González",
    role: "Fundador",
    image: "/images/team/nelson_gonzalez.jpg",
    bio: "Nelson pasó años dependiendo del alcohol y la cocaína. Perdió trabajos. Perdió tiempo con sus hijos. Pasó por centros de rehabilitación que lo ayudaron y por otros que lo humillaron y le hicieron más daño que bien. Cuando salió, descubrió que el problema no era solo la adicción. Era la desinformación que rodea a las familias en el peor momento de sus vidas.",
    bio2: "Ninguna plataforma comparaba los centros con información verificada, accesible y sin letra chica. Eso fue lo que decidió construir. Hoy lleva años de sobriedad y, en vez de quedarse solo con su recuperación, la convirtió en algo útil para los demás. Eso tiene un nombre: propósito. Y según la evidencia, es el factor protector más potente contra la recaída."
  },
  {
    name: "Ricardo Manzur Carrasco",
    role: "Comunicaciones",
    image: "/images/team/ricardo_manzur.png",
    bio: "Ricardo fue editor nacional de un diario durante más de una década con dependencia severa al alcohol. Nadie lo vio. Nadie hizo la pregunta correcta. Cuando pidió ayuda, no sabía si iba a poder salir.",
    bio2: "Hoy es periodista especializado en salud mental y adicciones, certificado por OPS/OMS y SENDA en neurobiología del consumo y política de drogas. Lleva dos décadas construyendo historias que cambian lo que la gente decide hacer. Trabaja con clínicas y centros de rehabilitación que quieren dejar de comunicar por inercia y empezar a hacerlo con estrategia. Y aporta algo que ningún consultor de comunicaciones puede estudiar: sabe cómo se siente estar adentro."
  },
  {
    name: "Héctor Marín",
    role: "Terapeuta en Adicciones",
    image: "/images/team/hector_marin.jpg",
    bio: "Héctor lleva más de 15 años dedicado a la intervención terapéutica en adicciones. Ha trabajado en Venezuela, Perú y Chile, conociendo de primera mano las realidades de distintos países y los diferentes modelos de tratamiento que existen: desde programas residenciales intensivos hasta intervenciones ambulatorias y de reducción de daños.",
    bio2: "En SinAdicciones.org, Héctor cumple un rol clave: orientar a usuarios y familias para encontrar el tratamiento más adecuado según su historia de consumo, contexto familiar y posibilidades económicas. No existe un solo camino hacia la recuperación, y él lo sabe. Por eso escucha antes de recomendar, y acompaña antes de derivar."
  }
];

export default function QuienesSomosPage() {
  return (
    <div className="min-h-screen bg-slate-950">
      <title>Quiénes Somos | SinAdicciones.org - Misión, Visión y Propósito</title>
        <meta name="description" content="Conoce al equipo detrás de SinAdicciones.org. Personas que vivieron la adicción desde adentro y decidieron que esa experiencia tenía que servir para algo más que sobrevivir." />
        <meta name="keywords" content="centros rehabilitación Chile, clínicas adicciones, tratamiento drogas, alcoholismo, psicólogo adicciones, desintoxicación, centros gratuitos, tratamiento residencial, ambulatorio, reducción de daño, test dependencia" />
        <link rel="canonical" href="https://sinadicciones.org/quienes-somos" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/quienes-somos" />
        <meta property="og:title" content="Quiénes Somos — SinAdicciones.org" />
        <meta property="og:description" content="Tres personas que vivieron la adicción desde adentro y decidieron que esa experiencia tenía que servir para algo más que sobrevivir." />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Quiénes Somos — SinAdicciones.org" />
        <meta name="twitter:description" content="Tres personas que vivieron la adicción y decidieron que tenía que servir para algo más." />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
      <Header />
      
      <main className="pt-20">
        {/* Hero */}
        <section className="py-16 px-4 bg-gradient-to-b from-slate-900 to-slate-950">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-emerald-400" />
            </div>
            <p className="text-emerald-400 font-medium mb-2">SinAdicciones.org</p>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Quiénes somos, misión, visión y propósito
            </h1>
          </div>
        </section>

        {/* Quiénes Somos */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-8">Quiénes somos</h2>
              
              <div className="space-y-6 text-lg">
                <p className="text-slate-300 leading-relaxed">
                  <span className="text-emerald-400 font-semibold">SinAdicciones.org</span> no nació en una sala de reuniones. Nació en el punto más bajo de tres historias distintas que terminaron, contra todo pronóstico, en el mismo lugar: <strong className="text-white">la recuperación</strong>.
                </p>
                
                <p className="text-slate-400 leading-relaxed">
                  Somos el directorio más completo de centros de rehabilitación, profesionales y talleres terapéuticos para adicciones en Chile. Más de 200 centros verificados en las 16 regiones del país, con información clara, actualizada y sin letra chica. Pero antes de ser una plataforma, somos personas que vivieron esto desde adentro y que decidieron que esa experiencia tenía que servir para algo más que sobrevivir.
                </p>
              </div>
            </div>

            {/* Team Members */}
            <div className="space-y-16">
              {teamMembers.map((member, index) => (
                <div key={member.name} className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8">
                  <div className="flex flex-col md:flex-row gap-8">
                    {/* Photo */}
                    <div className="flex-shrink-0 mx-auto md:mx-0">
                      <div className="w-40 h-40 rounded-full overflow-hidden border-4 border-emerald-500/30 shadow-lg shadow-emerald-500/10">
                        <img 
                          src={member.image} 
                          alt={member.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1">
                      <div className="mb-4">
                        <h3 className="text-2xl font-bold text-white">{member.name}</h3>
                        <p className="text-emerald-400 font-medium">{member.role}</p>
                      </div>
                      
                      <div className="space-y-4 text-slate-400 leading-relaxed">
                        <p>{member.bio}</p>
                        <p>{member.bio2}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Separator */}
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-center gap-4 py-8">
            <span className="text-slate-600">· · ·</span>
          </div>
        </div>

        {/* Lo que nos hace distintos */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-white mb-8">Lo que nos hace distintos</h2>
              
              <div className="space-y-6 text-lg">
                <p className="text-slate-300 leading-relaxed">
                  En Chile hay psicólogos, psiquiatras, trabajadores sociales y académicos que hablan de adicciones. Todos tienen algo valioso que aportar. Nosotros tenemos algo distinto.
                </p>
                
                <div className="bg-slate-900/50 border-l-4 border-emerald-500 pl-6 py-4 my-8">
                  <Quote className="w-8 h-8 text-emerald-500/50 mb-4" />
                  <p className="text-white text-xl italic leading-relaxed">
                    Ningún profesional de la salud, por muy bien formado que esté, puede pararse frente a una familia en crisis y decir: yo estuve ahí. Yo perdí el control. Yo le mentí a las personas que más quiero. Yo llegué a un centro de rehabilitación sin saber si iba a poder salir. Y hoy estoy aquí, construyendo algo con esa experiencia.
                  </p>
                </div>
                
                <p className="text-slate-400 leading-relaxed">
                  Eso no se estudia. No se certifica. No se contrata en una agencia.
                </p>
                
                <p className="text-slate-400 leading-relaxed">
                  La evidencia clínica es clara: <strong className="text-white">el estigma es la principal barrera que impide que las personas busquen ayuda a tiempo</strong>. Y el estigma se reduce cuando quien habla del tema lo hace sin vergüenza, con nombre y apellido, desde adentro.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Separator */}
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-center gap-4 py-8">
            <span className="text-slate-600">· · ·</span>
          </div>
        </div>

        {/* Lo que puedes encontrar */}
        <section className="py-16 px-4 bg-slate-900/50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8">Lo que puedes encontrar en SinAdicciones.org</h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="flex items-start gap-4 p-6 bg-slate-800/50 rounded-xl border border-slate-700">
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Centros de rehabilitación</h3>
                  <p className="text-slate-400 text-sm">Compara más de 200 opciones verificadas por región, modalidad y precio.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 p-6 bg-slate-800/50 rounded-xl border border-slate-700">
                <div className="w-12 h-12 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                  <Brain className="w-6 h-6 text-sky-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Profesionales especializados</h3>
                  <p className="text-slate-400 text-sm">Psicólogos y psiquiatras verificados en nuestro directorio.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 p-6 bg-slate-800/50 rounded-xl border border-slate-700">
                <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Herramientas gratuitas</h3>
                  <p className="text-slate-400 text-sm">Test de dependencia al alcohol (AUDIT), test general de adicciones y calculadora de costos.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 p-6 bg-slate-800/50 rounded-xl border border-slate-700">
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Asesoría experta personalizada</h3>
                  <p className="text-slate-400 text-sm">Si no sabes por dónde empezar, nuestra guía te orienta paso a paso.</p>
                </div>
              </div>
            </div>
            
            <p className="text-slate-400 text-center">
              Y si trabajas en un centro de rehabilitación o eres profesional de la salud, puedes <Link to="/agregar-centro" className="text-emerald-400 hover:underline">sumarte al directorio</Link> más consultado de Chile.
            </p>
          </div>
        </section>

        {/* Misión */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-rose-500/20 flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-rose-400" />
              </div>
              <h2 className="text-3xl font-bold text-white">Misión</h2>
            </div>
            
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 text-center">
              <p className="text-xl text-white leading-relaxed mb-6">
                Acortar el camino entre la familia que busca ayuda y el lugar correcto para encontrarla, con información verificada, orientación honesta y la credibilidad de quienes lo vivieron desde adentro.
              </p>
              
              <p className="text-slate-400 leading-relaxed">
                En Chile, solo una de cada doce personas con dependencia recibe algún tipo de tratamiento. No es por falta de centros. Es porque encontrar el correcto, en el momento de crisis, con información confiable, es más difícil de lo que debería ser. <strong className="text-white">SinAdicciones.org existe para resolver exactamente eso.</strong>
              </p>
            </div>
          </div>
        </section>

        {/* Visión */}
        <section className="py-16 px-4 bg-slate-900/50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <Eye className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-3xl font-bold text-white">Visión</h2>
            </div>
            
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 text-center">
              <p className="text-xl text-white leading-relaxed mb-6">
                Ser la plataforma de referencia en América Latina para la orientación en adicciones y salud mental, construida desde la experiencia real y sostenida por la evidencia clínica.
              </p>
              
              <p className="text-slate-400 leading-relaxed">
                No queremos ser solo un directorio. Queremos ser el lugar al que acude una familia cuando no sabe por dónde empezar, el espacio donde un profesional de la salud encuentra recursos actualizados, y la voz que le dice a quien lo necesita que <strong className="text-emerald-400">pedir ayuda no es rendirse. Es el primer acto de valentía.</strong>
              </p>
            </div>
          </div>
        </section>

        {/* Propósito */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-purple-400" />
              </div>
              <h2 className="text-3xl font-bold text-white">Propósito</h2>
            </div>
            
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-8">
              <p className="text-xl text-white leading-relaxed mb-6 text-center">
                Cambiar el paradigma de cómo Chile entiende la adicción: de problema moral a condición de salud. De vergüenza a conversación. De estigma a oportunidad de recuperación.
              </p>
              
              <div className="space-y-4 text-slate-400 leading-relaxed">
                <p>
                  La adicción no es un defecto de carácter. No es falta de voluntad. No define a quien la padece. Es una condición clínica documentada, con base neurobiológica, que responde al tratamiento adecuado cuando se accede a él a tiempo y sin estigma.
                </p>
                
                <p>
                  En Chile, el debate público sobre adicciones todavía está más cerca del juicio moral que de la evidencia científica. Eso tiene consecuencias reales: personas que no buscan ayuda por miedo a ser juzgadas, familias que minimizan el problema hasta que ya es tarde, sistemas de salud que tratan el síntoma y no la enfermedad.
                </p>
                
                <p>
                  <strong className="text-white">Queremos cambiar eso.</strong> No con discursos. Con información verificada, con historias reales contadas sin vergüenza y con una plataforma que hace más fácil dar el primer paso.
                </p>
              </div>
              
              <div className="mt-8 p-6 bg-slate-900/50 rounded-xl text-center">
                <p className="text-xl text-emerald-400 font-medium">
                  Porque el primer paso es el más difícil.
                </p>
                <p className="text-white text-lg mt-2">
                  Y nosotros estamos aquí para que sea un poco menos difícil de lo que fue para nosotros.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4 bg-slate-900/50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-xl text-slate-300 mb-2">
                Si estás buscando ayuda, o quieres que alguien que quieres la encuentre…
              </p>
              <p className="text-2xl font-bold text-emerald-400">
                Estás en el lugar correcto.
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link 
                to="/centros-de-rehabilitacion-drogas-chile"
                className="flex items-center gap-3 p-4 bg-slate-900 rounded-xl border border-slate-800 hover:border-emerald-500/50 transition-all group"
              >
                <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                  <Search className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <p className="text-white font-medium group-hover:text-emerald-400 transition-colors">Explorar Directorio</p>
                  <p className="text-slate-500 text-xs">Centros verificados</p>
                </div>
              </Link>
              
              <Link 
                to="/asesoria"
                className="flex items-center gap-3 p-4 bg-slate-900 rounded-xl border border-slate-800 hover:border-purple-500/50 transition-all group"
              >
                <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                  <Brain className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <p className="text-white font-medium group-hover:text-purple-400 transition-colors">Asesoría Experta</p>
                  <p className="text-slate-500 text-xs">Orientación personalizada</p>
                </div>
              </Link>
              
              <Link 
                to="/herramientas/test-alcoholismo"
                className="flex items-center gap-3 p-4 bg-slate-900 rounded-xl border border-slate-800 hover:border-amber-500/50 transition-all group"
              >
                <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                  <CheckCircle className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-white font-medium group-hover:text-amber-400 transition-colors">Test AUDIT</p>
                  <p className="text-slate-500 text-xs">Dependencia al alcohol</p>
                </div>
              </Link>
              
              <a 
                href="tel:1412"
                className="flex items-center gap-3 p-4 bg-slate-900 rounded-xl border border-slate-800 hover:border-rose-500/50 transition-all group"
              >
                <div className="w-12 h-12 rounded-full bg-rose-500/10 flex items-center justify-center group-hover:bg-rose-500/20 transition-colors">
                  <Phone className="w-6 h-6 text-rose-400" />
                </div>
                <div>
                  <p className="text-white font-medium group-hover:text-rose-400 transition-colors">Llama al 1412</p>
                  <p className="text-slate-500 text-xs">Si es urgente</p>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* Redes Sociales */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-slate-50 mb-3">Síguenos en Redes Sociales</h2>
            <p className="text-slate-400 mb-8">Contenido diario sobre prevención, tratamiento y recuperación</p>
            <div className="flex items-center justify-center gap-4">
              <a href="https://www.instagram.com/sinadicciones_org/" target="_blank" rel="noopener noreferrer" className="group flex flex-col items-center gap-2 p-4 bg-slate-900 rounded-xl border border-slate-800 hover:border-pink-500/50 transition-all w-28" data-testid="quienes-social-instagram">
                <div className="w-12 h-12 rounded-full bg-pink-500/10 group-hover:bg-pink-500/20 flex items-center justify-center transition-colors">
                  <svg className="w-6 h-6 text-pink-400" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </div>
                <span className="text-slate-400 group-hover:text-pink-400 text-xs font-medium transition-colors">Instagram</span>
              </a>
              <a href="https://web.facebook.com/profile.php?id=61585407995202" target="_blank" rel="noopener noreferrer" className="group flex flex-col items-center gap-2 p-4 bg-slate-900 rounded-xl border border-slate-800 hover:border-blue-500/50 transition-all w-28" data-testid="quienes-social-facebook">
                <div className="w-12 h-12 rounded-full bg-blue-500/10 group-hover:bg-blue-500/20 flex items-center justify-center transition-colors">
                  <svg className="w-6 h-6 text-blue-400" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </div>
                <span className="text-slate-400 group-hover:text-blue-400 text-xs font-medium transition-colors">Facebook</span>
              </a>
              <a href="https://www.tiktok.com/@sinadicciones.org" target="_blank" rel="noopener noreferrer" className="group flex flex-col items-center gap-2 p-4 bg-slate-900 rounded-xl border border-slate-800 hover:border-slate-500 transition-all w-28" data-testid="quienes-social-tiktok">
                <div className="w-12 h-12 rounded-full bg-slate-800 group-hover:bg-slate-700 flex items-center justify-center transition-colors">
                  <svg className="w-6 h-6 text-slate-300" viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                </div>
                <span className="text-slate-400 group-hover:text-slate-200 text-xs font-medium transition-colors">TikTok</span>
              </a>
              <a href="https://www.youtube.com/@sinadicciones_org" target="_blank" rel="noopener noreferrer" className="group flex flex-col items-center gap-2 p-4 bg-slate-900 rounded-xl border border-slate-800 hover:border-red-500/50 transition-all w-28" data-testid="quienes-social-youtube">
                <div className="w-12 h-12 rounded-full bg-red-500/10 group-hover:bg-red-500/20 flex items-center justify-center transition-colors">
                  <svg className="w-6 h-6 text-red-400" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </div>
                <span className="text-slate-400 group-hover:text-red-400 text-xs font-medium transition-colors">YouTube</span>
              </a>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
